/* eslint-disable @typescript-eslint/no-explicit-any */
import Input from "components/Input";
import Button from "components/Button";
import React, { useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import Select from "react-select";
import apiService from "services/apiService";
import { useToaster } from "contexts/toasterContext";

interface TrackingSettings {
  screenshotEnabled: boolean;
  deviceCheckInterval: number;
  dailyBreakLimit: number;
  sessionUploadInterval: number;
  urlSessionUploadInterval: number;
  deviceStatusUploadInterval: number;
  presenceCheckInterval: number;
  presenceUploadInterval: number;
  scheduleTrackingEnabled: boolean;
  idleThreshold: number;
  screenshotInterval: number;
  workStartTime: string;
  workEndTime: string;
  workDays: number[];
  dashboardEnabled?: boolean;
  screenshotQuality?: string;
  appTrackingEnabled?: boolean;
  urlTrackingEnabled?: boolean;
  presenceTrackingEnabled?: boolean;
  deviceTrackingEnabled?: boolean;
  wifiTrackingEnabled?: boolean;
  liveScreenEnabled?: boolean;
  liveScreenQuality?: string;
}

const DAYS_OF_WEEK = [
  { value: 1, label: "Monday" },
  { value: 2, label: "Tuesday" },
  { value: 3, label: "Wednesday" },
  { value: 4, label: "Thursday" },
  { value: 5, label: "Friday" },
  { value: 6, label: "Saturday" },
  { value: 0, label: "Sunday" },
];

const QUALITY_OPTIONS = [
  {
    value: "low",
    label: "Low",
    resolution: "960 × 540",
    size: "5 fps",
    color: "text-green-600",
  },
  {
    value: "medium",
    label: "Medium",
    resolution: "1280 × 720",
    size: "10 fps",
    color: "text-orange-500",
  },
  {
    value: "high",
    label: "High",
    resolution: "1920 × 1080",
    size: "20 fps",
    color: "text-red-600",
  },
];

const formatQualityOption = (data: any, { context }: any) => {
  if (context === "value") {
    return (
      <div className="flex items-center text-sm font-medium">
        <svg
          className="w-4 h-4 mr-2 text-blue-600"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="2"
            d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
          />
        </svg>
        <span className="text-gray-900 dark:text-white">{data.label}</span>
        <span className="mx-2 text-gray-400">•</span>
        <span className="text-gray-500 dark:text-gray-400">{data.resolution}</span>
        <span className="mx-2 text-gray-400">•</span>
        <span className="text-gray-500 dark:text-gray-400">{data.size}</span>
      </div>
    );
  }

  return (
    <div className="flex items-center justify-between text-sm">
      <div className="flex items-center w-1/3">
        <svg
          className={`w-5 h-5 mr-2 ${data.color}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="2"
            d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
          />
        </svg>
        <span className="font-semibold text-gray-900 dark:text-white">
          {data.label}
        </span>
      </div>
      <div className="w-1/3 text-gray-600 dark:text-gray-400 flex items-center">
        <span className="mr-1.5 text-gray-400">•</span> {data.resolution}
      </div>
      <div className="w-1/3 text-gray-600 dark:text-gray-400 flex items-center">
        <span className="mr-1.5 text-gray-400">•</span> {data.size}
      </div>
    </div>
  );
};

interface AddSettingsProps {
  onSuccess?: () => void;
}

const AddSettings: React.FC<AddSettingsProps> = ({ onSuccess }) => {
  const methods = useForm<TrackingSettings>({
    defaultValues: {
      screenshotEnabled: false,
      deviceCheckInterval: 10800,
      dailyBreakLimit: 5400,
      sessionUploadInterval: 3600,
      urlSessionUploadInterval: 3600,
      deviceStatusUploadInterval: 600,
      presenceCheckInterval: 600,
      presenceUploadInterval: 3600,
      scheduleTrackingEnabled: true,
      idleThreshold: 130,
      screenshotInterval: 600,
      workStartTime: "10:00",
      workEndTime: "18:00",
      workDays: [1, 2, 3, 4, 5],
    },
  });

  const [selectedDays, setSelectedDays] = useState(
    DAYS_OF_WEEK.filter((day) => [1, 2, 3, 4, 5].includes(day.value)),
  );

  const [screenshotEnabled, setScreenshotEnabled] = useState(false);
  const [screenshotQuality, setScreenshotQuality] = useState(QUALITY_OPTIONS[1]);
  const [scheduleTrackingEnabled, setScheduleTrackingEnabled] = useState(true);

  // Live screen state
  const [liveScreenEnabled, setLiveScreenEnabled] = useState(false);
  const [liveScreenQuality, setLiveScreenQuality] = useState(QUALITY_OPTIONS[1]);

  // Dashboard & Feature Control state
  const [dashboardEnabled, setDashboardEnabled] = useState(false);
  const [appTrackingEnabled, setAppTrackingEnabled] = useState(false);
  const [urlTrackingEnabled, setUrlTrackingEnabled] = useState(true);
  const [presenceTrackingEnabled, setPresenceTrackingEnabled] = useState(false);
  const [deviceTrackingEnabled, setDeviceTrackingEnabled] = useState(true);
  const [wifiTrackingEnabled, setWifiTrackingEnabled] = useState(true);

  const [isSubmitting, setIsSubmitting] = useState(false);
  const { addToast } = useToaster();

  const onSubmit = async (data: TrackingSettings) => {
    try {
      setIsSubmitting(true);
      const formData = {
        ...data,
        workDays: selectedDays.map((day) => day.value),
        screenshotEnabled,
        screenshotQuality: screenshotQuality.value.toUpperCase(),
        scheduleTrackingEnabled,
        liveScreenEnabled,
        liveScreenQuality: liveScreenQuality.value.toUpperCase(),
        dashboardEnabled,
        appTrackingEnabled,
        urlTrackingEnabled,
        presenceTrackingEnabled,
        deviceTrackingEnabled,
        wifiTrackingEnabled,
      };

      const response = await apiService.post(
        "/api/tracking-global-settings/update",
        formData,
      );

      if (response.status === 200 || response.status === 201) {
        addToast("Tracking settings updated successfully!", "success");
        if (onSuccess) {
          onSuccess();
        }
      }
    } catch (error: any) {
      console.error("Error updating tracking settings:", error);
      addToast(
        error?.response?.data?.message || "Failed to update tracking settings",
        "error",
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleReset = () => {
    methods.reset();
    setScreenshotEnabled(false);
    setScreenshotQuality(QUALITY_OPTIONS[1]);
    setScheduleTrackingEnabled(true);
    setLiveScreenEnabled(false);
    setLiveScreenQuality(QUALITY_OPTIONS[1]);
    setDashboardEnabled(false);
    setAppTrackingEnabled(false);
    setUrlTrackingEnabled(true);
    setPresenceTrackingEnabled(false);
    setDeviceTrackingEnabled(true);
    setWifiTrackingEnabled(true);
    setSelectedDays(DAYS_OF_WEEK.filter((day) => [1, 2, 3, 4, 5].includes(day.value)));
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 py-4 px-4">
      <div className="max-w-7xl mx-auto">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6">
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
            Tracking Settings
          </h1>
          <p className="text-gray-600 dark:text-gray-400 mb-8">
            Configure your employee tracking and monitoring preferences
          </p>

          <FormProvider {...methods}>
            <form
              onSubmit={methods.handleSubmit(onSubmit)}
              className="space-y-6"
            >
              {/* Screenshot Settings Section */}
              <div className="border-b border-gray-200 dark:border-gray-700 pb-6">
                <h2 className="text-xl font-semibold text-gray-800 dark:text-white mb-4 flex items-center">
                  <svg
                    className="w-6 h-6 mr-2 text-blue-600"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z"
                    />
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M15 13a3 3 0 11-6 0 3 3 0 016 0z"
                    />
                  </svg>
                  Screenshot Settings
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 items-start">
                  <div className="flex items-center pt-3 pb-3 md:pb-0">
                    <input
                      type="checkbox"
                      id="screenshotEnabled"
                      checked={screenshotEnabled}
                      onChange={(e) => setScreenshotEnabled(e.target.checked)}
                      className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 dark:focus:ring-blue-600 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600"
                    />
                    <label
                      htmlFor="screenshotEnabled"
                      className="ml-2 text-sm font-medium text-gray-900 dark:text-gray-300"
                    >
                      Enable Screenshots
                    </label>
                  </div>
                  <Input
                    name="screenshotInterval"
                    label="Screenshot Interval (seconds)"
                    type="number"
                    placeholder="600"
                    min={60}
                  />

                  <div>
                    <label className="block mb-2 text-sm font-medium text-gray-900 dark:text-white">
                      Screenshot Quality
                    </label>
                    <Select
                      value={screenshotQuality}
                      onChange={(selected) => setScreenshotQuality(selected as any)}
                      options={QUALITY_OPTIONS}
                      formatOptionLabel={formatQualityOption}
                      classNamePrefix="select"
                      placeholder="Select quality..."
                      unstyled
                      classNames={{
                        control: (state) =>
                          `min-h-[42px] border rounded-lg px-2 ${state.isFocused
                            ? "border-blue-500 ring-1 ring-blue-500"
                            : "border-gray-300 dark:border-gray-600"
                          } bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-white transition-colors duration-200`,
                        menu: () =>
                          "mt-1 rounded-lg shadow-lg bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 z-50 overflow-hidden",
                        menuList: () => "py-0",
                        option: (state) =>
                          `px-3 py-2 cursor-pointer border-b border-gray-100 dark:border-gray-700/50 last:border-0 ${state.isFocused
                            ? "bg-gray-50 dark:bg-gray-700/50"
                            : "bg-white dark:bg-gray-800"
                          }`,
                        singleValue: () => "text-gray-900 dark:text-white",
                        input: () => "text-gray-900 dark:text-white m-0",
                        placeholder: () => "text-gray-500 dark:text-gray-400 mx-0.5",
                        clearIndicator: () => "p-1 cursor-pointer",
                        dropdownIndicator: () => "p-1 cursor-pointer",
                      }}
                    />
                  </div>
                </div>
              </div>

              {/* Live Screen Settings Section */}
              <div className="border-b border-gray-200 dark:border-gray-700 pb-6">
                <h2 className="text-xl font-semibold text-gray-800 dark:text-white mb-4 flex items-center">
                  <svg
                    className="w-6 h-6 mr-2 text-pink-600"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z"
                    />
                  </svg>
                  Live Screen Settings
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 items-start">
                  <div className="flex items-center pt-3 pb-3 md:pb-0 md:pt-[34px]">
                    <input
                      type="checkbox"
                      id="liveScreenEnabled"
                      checked={liveScreenEnabled}
                      onChange={(e) => setLiveScreenEnabled(e.target.checked)}
                      className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 dark:focus:ring-blue-600 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600"
                    />
                    <label
                      htmlFor="liveScreenEnabled"
                      className="ml-2 text-sm font-medium text-gray-900 dark:text-gray-300"
                    >
                      Enable Live Screen
                    </label>
                  </div>

                  <div>
                    <label className="block mb-2 text-sm font-medium text-gray-900 dark:text-white">
                      Live Screen Quality
                    </label>
                    <Select
                      value={liveScreenQuality}
                      onChange={(selected) => setLiveScreenQuality(selected as any)}
                      options={QUALITY_OPTIONS}
                      formatOptionLabel={formatQualityOption}
                      classNamePrefix="select"
                      placeholder="Select quality..."
                      unstyled
                      classNames={{
                        control: (state) =>
                          `min-h-[42px] border rounded-lg px-2 ${state.isFocused
                            ? "border-blue-500 ring-1 ring-blue-500"
                            : "border-gray-300 dark:border-gray-600"
                          } bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-white transition-colors duration-200`,
                        menu: () =>
                          "mt-1 rounded-lg shadow-lg bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 z-50 overflow-hidden",
                        menuList: () => "py-0",
                        option: (state) =>
                          `px-3 py-2 cursor-pointer border-b border-gray-100 dark:border-gray-700/50 last:border-0 ${state.isFocused
                            ? "bg-gray-50 dark:bg-gray-700/50"
                            : "bg-white dark:bg-gray-800"
                          }`,
                        singleValue: () => "text-gray-900 dark:text-white",
                        input: () => "text-gray-900 dark:text-white m-0",
                        placeholder: () => "text-gray-500 dark:text-gray-400 mx-0.5",
                        clearIndicator: () => "p-1 cursor-pointer",
                        dropdownIndicator: () => "p-1 cursor-pointer",
                      }}
                    />
                  </div>
                </div>
              </div>

              {/* User Dashboard Settings Section */}
              <div className="border-b border-gray-200 dark:border-gray-700 pb-6">
                <h2 className="text-xl font-semibold text-gray-800 dark:text-white mb-4 flex items-center">
                  <svg
                    className="w-6 h-6 mr-2 text-teal-600"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z"
                    />
                  </svg>
                  User Dashboard Settings
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="dashboardEnabled"
                      checked={dashboardEnabled}
                      onChange={(e) => setDashboardEnabled(e.target.checked)}
                      className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 dark:focus:ring-blue-600 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600"
                    />
                    <label
                      htmlFor="dashboardEnabled"
                      className="ml-2 text-sm font-medium text-gray-900 dark:text-gray-300"
                    >
                      Enable User Dashboard
                    </label>
                  </div>
                </div>
              </div>

              {/* Feature Control Section */}
              <div className="border-b border-gray-200 dark:border-gray-700 pb-6">
                <h2 className="text-xl font-semibold text-gray-800 dark:text-white mb-4 flex items-center">
                  <svg
                    className="w-6 h-6 mr-2 text-indigo-600"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4"
                    />
                  </svg>
                  Feature Control
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="appTrackingEnabled"
                      checked={appTrackingEnabled}
                      onChange={(e) => setAppTrackingEnabled(e.target.checked)}
                      className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 dark:focus:ring-blue-600 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600"
                    />
                    <label
                      htmlFor="appTrackingEnabled"
                      className="ml-2 text-sm font-medium text-gray-900 dark:text-gray-300"
                    >
                      Enable App Tracking
                    </label>
                  </div>

                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="urlTrackingEnabled"
                      checked={urlTrackingEnabled}
                      onChange={(e) => setUrlTrackingEnabled(e.target.checked)}
                      className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 dark:focus:ring-blue-600 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600"
                    />
                    <label
                      htmlFor="urlTrackingEnabled"
                      className="ml-2 text-sm font-medium text-gray-900 dark:text-gray-300"
                    >
                      Enable URL Tracking
                    </label>
                  </div>

                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="presenceTrackingEnabled"
                      checked={presenceTrackingEnabled}
                      onChange={(e) => setPresenceTrackingEnabled(e.target.checked)}
                      className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 dark:focus:ring-blue-600 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600"
                    />
                    <label
                      htmlFor="presenceTrackingEnabled"
                      className="ml-2 text-sm font-medium text-gray-900 dark:text-gray-300"
                    >
                      Face Detection
                    </label>
                  </div>

                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="deviceTrackingEnabled"
                      checked={deviceTrackingEnabled}
                      onChange={(e) => setDeviceTrackingEnabled(e.target.checked)}
                      className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 dark:focus:ring-blue-600 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600"
                    />
                    <label
                      htmlFor="deviceTrackingEnabled"
                      className="ml-2 text-sm font-medium text-gray-900 dark:text-gray-300"
                    >
                      Enable Device Tracking
                    </label>
                  </div>

                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="wifiTrackingEnabled"
                      checked={wifiTrackingEnabled}
                      onChange={(e) => setWifiTrackingEnabled(e.target.checked)}
                      className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 dark:focus:ring-blue-600 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600"
                    />
                    <label
                      htmlFor="wifiTrackingEnabled"
                      className="ml-2 text-sm font-medium text-gray-900 dark:text-gray-300"
                    >
                      Enable WiFi Tracking
                    </label>
                  </div>
                </div>
              </div>

              {/* Time Intervals Section */}
              <div className="border-b border-gray-200 dark:border-gray-700 pb-6">
                <h2 className="text-xl font-semibold text-gray-800 dark:text-white mb-4 flex items-center">
                  <svg
                    className="w-6 h-6 mr-2 text-green-600"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                  Time Intervals
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  <Input
                    name="deviceCheckInterval"
                    label="Device Check Interval (seconds)"
                    type="number"
                    placeholder="10800"
                    min={60}
                  />
                  <Input
                    name="sessionUploadInterval"
                    label="Session Upload Interval (seconds)"
                    type="number"
                    placeholder="3600"
                    min={60}
                  />
                  <Input
                    name="urlSessionUploadInterval"
                    label="URL Session Upload Interval (seconds)"
                    type="number"
                    placeholder="3600"
                    min={60}
                  />
                  <Input
                    name="deviceStatusUploadInterval"
                    label="Device Status Upload Interval (seconds)"
                    type="number"
                    placeholder="600"
                    min={60}
                  />
                  <Input
                    name="presenceCheckInterval"
                    label="Presence Check Interval (seconds)"
                    type="number"
                    placeholder="600"
                    min={60}
                  />
                  <Input
                    name="presenceUploadInterval"
                    label="Presence Upload Interval (seconds)"
                    type="number"
                    placeholder="3600"
                    min={60}
                  />
                </div>
              </div>

              {/* Work Schedule Section */}
              <div className="border-b border-gray-200 dark:border-gray-700 pb-6">
                <h2 className="text-xl font-semibold text-gray-800 dark:text-white mb-4 flex items-center">
                  <svg
                    className="w-6 h-6 mr-2 text-purple-600"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                    />
                  </svg>
                  Work Schedule
                </h2>
                <div className="space-y-6">
                  <div className="flex items-center mb-4">
                    <input
                      type="checkbox"
                      id="scheduleTrackingEnabled"
                      checked={scheduleTrackingEnabled}
                      onChange={(e) =>
                        setScheduleTrackingEnabled(e.target.checked)
                      }
                      className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 dark:focus:ring-blue-600 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600"
                    />
                    <label
                      htmlFor="scheduleTrackingEnabled"
                      className="ml-2 text-sm font-medium text-gray-900 dark:text-gray-300"
                    >
                      Enable Schedule Tracking
                    </label>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <Input
                      name="workStartTime"
                      label="Work Start Time"
                      type="time"
                      placeholder="10:00"
                    />
                    <Input
                      name="workEndTime"
                      label="Work End Time"
                      type="time"
                      placeholder="18:00"
                    />
                  </div>

                  <div>
                    <label className="block mb-2 text-sm font-medium text-gray-900 dark:text-white">
                      Work Days <span className="text-red-500">*</span>
                    </label>
                    <Select
                      isMulti
                      value={selectedDays}
                      onChange={(selected) => setSelectedDays(selected as any)}
                      options={DAYS_OF_WEEK}
                      className="basic-multi-select"
                      classNamePrefix="select"
                      placeholder="Select work days..."
                      styles={{
                        control: (base) => ({
                          ...base,
                          backgroundColor: "rgb(249 250 251)",
                          borderColor: "rgb(209 213 219)",
                          minHeight: "42px",
                          "&:hover": {
                            borderColor: "rgb(156 163 175)",
                          },
                        }),
                        menu: (base) => ({
                          ...base,
                          backgroundColor: "white",
                        }),
                        multiValue: (base) => ({
                          ...base,
                          backgroundColor: "rgb(59 130 246)",
                        }),
                        multiValueLabel: (base) => ({
                          ...base,
                          color: "white",
                        }),
                        multiValueRemove: (base) => ({
                          ...base,
                          color: "white",
                          "&:hover": {
                            backgroundColor: "rgb(37 99 235)",
                            color: "white",
                          },
                        }),
                      }}
                    />
                    <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
                      Select the days of the week when tracking should be active
                    </p>
                  </div>
                </div>
              </div>

              {/* Behavior Settings Section */}
              <div className="pb-6">
                <h2 className="text-xl font-semibold text-gray-800 dark:text-white mb-4 flex items-center">
                  <svg
                    className="w-6 h-6 mr-2 text-orange-600"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"
                    />
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                    />
                  </svg>
                  Behaviour Settings
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <Input
                    name="idleThreshold"
                    label="Idle Threshold (seconds)"
                    type="number"
                    placeholder="130"
                    min={30}
                  />
                  <Input
                    name="dailyBreakLimit"
                    label="Daily Break Limit (seconds)"
                    type="number"
                    placeholder="5400"
                    min={0}
                  />
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex justify-end space-x-4 pt-6 border-t border-gray-200 dark:border-gray-700">
                <Button
                  type="button"
                  color="gray"
                  onClick={handleReset}
                  disabled={isSubmitting}
                  className="bg-gray-500 hover:bg-gray-600"
                >
                  Reset
                </Button>
                <Button type="submit" color="blue" disabled={isSubmitting}>
                  {isSubmitting ? (
                    <span className="flex items-center">
                      <svg
                        className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                      >
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                        ></circle>
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        ></path>
                      </svg>
                      Saving...
                    </span>
                  ) : (
                    "Save Settings"
                  )}
                </Button>
              </div>
            </form>
          </FormProvider>
        </div>
      </div>
    </div>
  );
};

export default AddSettings;